import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  superadminBillingService,
  type AdminRefundRequest,
} from '@/services/superadminBilling';
import { RotateCcw, RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'refunded'] as const;

const SuperadminRefundRequestsPage: React.FC = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<AdminRefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await superadminBillingService.listRefundRequests({
        status: statusFilter || undefined,
      });
      setRows(data);
      const drafts: Record<string, string> = {};
      for (const r of data) {
        drafts[r.id] = r.adminNotes ?? '';
      }
      setNotesDraft(drafts);
    } catch {
      toast({ title: 'Error', description: 'Failed to load refund requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStatus = async (id: string, status: string) => {
    setBusyId(id);
    try {
      await superadminBillingService.updateRefundRequest(id, {
        status,
        adminNotes: notesDraft[id]?.trim() || undefined,
      });
      toast({
        title: 'Updated',
        description:
          status === 'refunded'
            ? 'Marked refunded — confirm the refund in Lemon Squeezy.'
            : `Status set to ${status}.`,
      });
      await load();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Could not update request';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Refund requests - Arrangelly Dashboard</title>
      </Helmet>
      <DashboardLayout>
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <RotateCcw className="w-5 h-5" />
                Refund requests
              </CardTitle>
              <CardDescription>
                Student 14-day refund requests. Process approved refunds in the Lemon Squeezy
                dashboard, then mark as refunded here.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <label className="text-sm text-slate-600 flex items-center gap-2">
                  Status
                  <select
                    className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {loading ? (
                <p className="text-gray-400 text-sm">Loading…</p>
              ) : rows.length === 0 ? (
                <p className="text-gray-400 text-sm">No refund requests for this filter.</p>
              ) : (
                <ul className="space-y-4">
                  {rows.map((r) => (
                    <li
                      key={r.id}
                      className="rounded-lg border border-slate-200 p-4 space-y-2 text-sm"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <div>
                          <p className="font-medium text-foreground">
                            {r.studentDisplayName ?? r.studentAuthUserId.slice(0, 8)}
                            <span className="text-slate-500 font-normal">
                              {' '}
                              · {r.tenantName ?? r.tenantId.slice(0, 8)}
                            </span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {r.productKind} · {r.status} ·{' '}
                            {new Date(r.createdAt).toLocaleString()}
                            {r.subscriptionId
                              ? ` · sub ${r.subscriptionId.slice(0, 8)}…`
                              : ''}
                          </p>
                          {r.reason && (
                            <p className="text-xs text-slate-600 mt-1">Reason: {r.reason}</p>
                          )}
                        </div>
                      </div>
                      <label className="block text-xs text-slate-600">
                        Admin notes
                        <textarea
                          className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm min-h-[60px]"
                          value={notesDraft[r.id] ?? ''}
                          onChange={(e) =>
                            setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))
                          }
                          placeholder="Lemon order ID, support ticket, etc."
                        />
                      </label>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {r.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              disabled={busyId === r.id}
                              onClick={() => void updateStatus(r.id, 'approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === r.id}
                              onClick={() => void updateStatus(r.id, 'rejected')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {(r.status === 'pending' || r.status === 'approved') && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busyId === r.id}
                            onClick={() => void updateStatus(r.id, 'refunded')}
                          >
                            Mark refunded
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SuperadminRefundRequestsPage;
